/**
 * Captura de UTM.
 *
 * O briefing exige UTMs nos links da campanha de e-mail e dos posts de LinkedIn.
 * Sem gravar esses parâmetros junto do lead, a campanha manda tráfego e ninguém
 * consegue dizer de onde veio cada inscrito — o que torna a própria UTM inútil.
 *
 * Guardado em sessionStorage porque a inscrição pode não acontecer no primeiro
 * pageview: a pessoa chega pelo anúncio, lê a página, sai, volta pelo histórico
 * (agora sem os parâmetros na URL) e só então se inscreve. Sem a persistência,
 * o segundo acesso perderia a atribuição.
 *
 * sessionStorage e não localStorage: a atribuição vale para esta visita. Guardar
 * para sempre faria uma inscrição de novembro ser creditada ao anúncio de agosto.
 */

const CHAVE = 'rota:utm'
const PARAMETROS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']

/** Lê a query string atual e persiste o que encontrar. Chamado no carregamento. */
export function capturarUtm() {
  if (typeof window === 'undefined') return

  const busca = new URLSearchParams(window.location.search)
  const achados = {}
  for (const p of PARAMETROS) {
    const valor = busca.get(p)
    // Limite de 200 caracteres: a coluna é JSON, mas uma URL montada à mão pode
    // trazer um parâmetro gigante e não há motivo para carregá-lo adiante.
    if (valor) achados[p] = valor.slice(0, 200)
  }

  let existente = null
  try {
    const bruto = window.sessionStorage.getItem(CHAVE)
    existente = bruto ? JSON.parse(bruto) : null
  } catch {
    existente = null
  }

  /* Referrer e página de entrada só valem para a PRIMEIRA captura da sessão
     (chamado é `onClientEntry`, mas roda de novo em todo `onRouteUpdate` —
     ver gatsby-browser.js). No segundo pageview em diante o referrer do
     navegador ainda seria o externo original, mas recalcular a cada troca de
     rota é trabalho sem propósito: a origem da VISITA não muda navegando
     dentro do próprio site. `existente` é null só na primeira chamada. */
  if (!existente) {
    if (document.referrer) achados.referrer = document.referrer.slice(0, 300)
    achados.pagina_entrada = `${window.location.pathname}${window.location.search}`.slice(0, 300)
  }

  // Sem nada novo pra guardar, NÃO apaga o que já estava — navegar entre
  // âncoras ou recarregar a página não pode zerar a atribuição da visita.
  if (Object.keys(achados).length === 0) return

  const combinado = { ...existente, ...achados }

  try {
    window.sessionStorage.setItem(CHAVE, JSON.stringify(combinado))
  } catch {
    // Modo privativo em alguns navegadores recusa a escrita. A inscrição é mais
    // importante que a atribuição: segue sem gravar.
  }
}

/** Devolve o que foi capturado nesta sessão, ou null. */
export function lerUtm() {
  if (typeof window === 'undefined') return null
  try {
    const bruto = window.sessionStorage.getItem(CHAVE)
    return bruto ? JSON.parse(bruto) : null
  } catch {
    return null
  }
}
